/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { GridRestService } from '@gh/common-shared-data';
import { REGION_CODE } from '@gh/config';
import { TranslationService } from '@gh/core-mls';
import { GridColumn, GridLayout, ColumnEnableConditions, GridLayoutService, ManagedGridLayout } from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';
import { defaults, intersectionWith, isNil } from 'lodash';
import { Observable } from 'rxjs';
import { GridSettingsService } from '../services';
import { of$ } from '@gh/rx';

const DEFAULT_PREDEFINED_COLUMN_FIELDS = {
  resizable: true,
  sortable: true,
  visible: true,
  width: 200,
};

const isSameColumn = (f: GridColumn, s: GridColumn) =>
  f.column === s.column && f.resizable === s.resizable && f.sortable === s.sortable;

/**
 * If number of columns, column name and column definitions are the same we return true, otherwise false
 */
const isGridLayoutSimilar = (defaultLayout: GridLayout, loadedLayout: GridLayout) =>
  defaultLayout.columns.length === loadedLayout.columns.length
  // tslint:disable-next-line:max-line-length
  && intersectionWith(defaultLayout.columns,
                      loadedLayout.columns,
                      isSameColumn).length === defaultLayout.columns.length;

/**
 * This function returns loaded layout only if it has the same columns.
 */
function mergeLoadedLayout(defaultLayout: GridLayout, loadedLayout: GridLayout): GridLayout {
  return isGridLayoutSimilar(defaultLayout, loadedLayout) ? loadedLayout : defaultLayout;
}

@Injectable()
export class DefaultGridLayoutService implements GridLayoutService {
  constructor(private gridRest: GridRestService,
              private gridService: GridSettingsService,
              private translations: TranslationService) {
  }

  get(name: string, columnEnableConditions?: ColumnEnableConditions): Observable<GridLayout> {
    const defaultLayout = this.loadDefaultLayout(name, columnEnableConditions);
    return this.gridRest.get(name).pipe(
      // tslint:disable-next-line:max-line-length
      map$((layout) => isNotNil(layout) ? mergeLoadedLayout(defaultLayout, this.filterAvailableColumns(layout, columnEnableConditions)) : defaultLayout),
      map$(ManagedGridLayout.create));
  }

  getJobGrids(name: string, columnEnableConditions?: ColumnEnableConditions): Observable<GridLayout> {
    const defaultLayout = this.loadDefaultLayout(name);
    return this.gridRest.getJobGrids(name).pipe(
      // tslint:disable-next-line:max-line-length
      map$((layout) => isNotNil(layout) ? mergeLoadedLayout(defaultLayout, this.filterAvailableColumns(layout, columnEnableConditions)) : defaultLayout),
      map$(ManagedGridLayout.create));
  }

  update(layout: GridLayout): Observable<GridLayout> {
    const preset = this.gridService.getCurrentPreset();
    if (preset && preset.name !== 'PERSONAL') {
      return of$(layout);
    }
    return this.gridRest.update(layout);
  }

  private loadDefaultLayout(name: string, columnEnableConditions?: ColumnEnableConditions): GridLayout {
    const rawData: Partial<GridColumn>[] = this.findRawData(name);
    if (isNil(rawData)) {
      throw new Error(`Default layout for '${name}' is not defined`);
    }

    const predefined = !columnEnableConditions ? rawData : rawData.filter((column) => {
      if (!column.column) {
        return true;
      }
      return !columnEnableConditions[column.column] || columnEnableConditions[column.column]();
    });
    return {
      name,
      pageSize: 50,
      columns: predefined.map((column: any, i) => defaults(
        { position: i + 1 },
        column,
        DEFAULT_PREDEFINED_COLUMN_FIELDS)),
    };
  }

  private findRawData(name: string): Partial<GridColumn>[] {
    const regionalRawData = this.translations.getRaw(`grid:${name}{${REGION_CODE}}`);
    if (regionalRawData) {
      return regionalRawData;
    }

    return this.translations.getRaw(`grid:${name}`);
  }

  private filterAvailableColumns(layout: GridLayout, columnEnableConditions?: ColumnEnableConditions): GridLayout {
    const columns = !columnEnableConditions ? layout.columns : layout.columns .filter((column) => {
      if (!column.column) {
        return true;
      }
      return !columnEnableConditions[column.column] || columnEnableConditions[column.column]();
    });
    return {
      ...layout,
      columns,
    };
  }
}
